import {
  registerGridOverride,
  type CellDecorator,
  type CellDecoratorContext,
  type GridOverride,
  type GridOverrideDeps,
} from "../../components/dataGrid/gridOverrides.tsx";
import { updateGridCell } from "../../api/endpoints.ts";
import { getStatementInvoice } from "../../api/statementConfig.ts";
import { ParamType } from "../../types/grid.ts";
import { Button } from "../../components/ui/button.tsx";
import { fetchPayoutTarget, PayoutTargetPanel } from "./payoutTargetPanel.tsx";

/**
 * Per-grid override for the server-driven `statementInvoice` grid (over
 * S_Statement_Invoice_FN). The `Approved` cell shows one of four states off
 * `ApprovedAt` + `State` + `AllowApprovalInAdmin`: an Approve button (Closed + not yet approved), a
 * "✓ Approved" marker (ApprovedAt set), or an "Awaiting close" hint (still Open)
 * so an unavailable action explains itself. A fourth state, "Confirmed in Odoo", is the DEFAULT:
 * approval from Admin is opt-in per operator (config allowApprovalInAdmin, captured onto the
 * invoice row at generation), and with it off the accountant posts the draft in Odoo and SIP
 * records the invoice as posted on its next sweep. Only the button path mutates. There is no
 * dedicated approve method: confirming runs a Set on the `Approved` cell via
 * the grid's generic UpdateGridCell, which Web_Admin_Page_Data.AlternateUpdate
 * routes to S_Statement_ApprovePayout_SP (@iUserRefID auto-injected). The SP
 * records the approver, enforces operator scope, and emits the posting
 * datagram. This decorator is just the affordance + the rich confirm.
 */
function col(ctx: CellDecoratorContext, name: string): unknown {
  const lc = name.toLowerCase();
  for (const [k, idx] of Object.entries(ctx.colIndex)) {
    if (k.toLowerCase() === lc) return ctx.rowData[idx];
  }
  return undefined;
}

function str(ctx: CellDecoratorContext, name: string): string {
  const v = col(ctx, name);
  return v == null ? "" : String(v);
}

function date(s: string): string {
  return s.length >= 10 ? s.slice(0, 10) : s;
}

function money(v: unknown): string {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—";
}

function approveDecorator(deps: GridOverrideDeps): CellDecorator {
  return (node, ctx) => {
    const id = Number(col(ctx, "ID") ?? 0);
    if (id <= 0) return node;
    const state = str(ctx, "State");
    // Gate on ApprovedAt — it's the approval signal present in the grid.
    // (ApprovedBy is not a displayed column, so it reads as undefined here.)
    const approvedAt = str(ctx, "ApprovedAt");

    // Already approved → show the approval state, never the button.
    if (approvedAt) {
      const at = date(approvedAt);
      return <span title={`Approved on ${at}`} style={{ color: "var(--color-text-muted)" }}>✓ Approved {at}</span>;
    }

    // Approval switched off for this invoice → say where it happens instead. The value is
    // captured ON THE ROW at generation, which is what lets this render synchronously and always
    // agree with S_Statement_ApprovePayout_SP — which refuses the same case itself rather than
    // trusting the grid to hide the affordance, since the Set behind it is a generic
    // UpdateGridCell. "0" is the default and covers every row created before 2026-08-31.
    if (String(col(ctx, "AllowApprovalInAdmin") ?? "0") !== "1") {
      return (
        <span
          title={"Approval from Admin is switched off for this operator: the accountant confirms the draft "
            + "in Odoo, and the processor records the invoice as posted on its next sweep. Switch on "
            + "“Approve from Admin” in the statement config to approve here instead — it applies to "
            + "invoices generated after the change."}
          style={{ color: "var(--color-text-muted)" }}
        >
          Confirmed in Odoo
        </span>
      );
    }

    // Not yet closed → not approvable; say why instead of an inert button.
    if (state !== "Closed") {
      return (
        <span
          title="Approval opens once the billing period closes (State becomes Closed). This invoice is still forming."
          style={{ color: "var(--color-text-muted)" }}
        >
          Awaiting close
        </span>
      );
    }

    // Closed + unapproved → the action.
    // Does this invoice have a PAYOUT side? Every shape except a fees-only invoice does, so the
    // dialog's payout hint and the bank-details panel follow the shape rather than one string.
    // "FeeInvoice" is the pre-2026-08-31 name for "Fees" and still sits on older rows.
    const shape = str(ctx, "BillingMode").toLowerCase();
    const feeMode = shape === "fees" || shape === "feeinvoice";
    const currency = str(ctx, "Currency");
    // Both modes show the FEE total, which is the one money figure this grid actually holds.
    //
    // It used to show TotalPayout for the payout modes, and that column sums
    // RevenueShareAmount — a typed copy of the revenueLadder FEE entry, not a payout (see
    // statement-billing-design.md §3, reworked 2026-08-31). For a config without a ladder it is
    // 0.00, so the dialog promised a zero payout on invoices that were about to pay out properly.
    // The real payout is what the daily closes COLLECTED in the period less these fees, and the
    // collection lives in S_Statement_BookKeeping_Daily, summed at push time — not available here.
    const amount = money(col(ctx, "TotalFee"));
    const operator = str(ctx, "OperatorName");
    const period = `${date(str(ctx, "PeriodStart"))} → ${date(str(ctx, "PeriodEnd"))}`;

    const onApprove = async () => {
      // OperatorID isn't a column in this grid's config (only OperatorName is),
      // so read the operator + period from the invoice record itself — same
      // authoritative source the read-only info modal uses.
      const detail = await getStatementInvoice(id);
      const operatorID = Number(detail?.OperatorID ?? col(ctx, "OperatorID") ?? 0);
      const periodEnd = detail?.PeriodEnd ?? str(ctx, "PeriodEnd");
      const target = await fetchPayoutTarget(operatorID, periodEnd);
      const ok = await deps.choose<boolean>({
        title: `Approve invoice #${id}?`,
        body: (
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)", fontSize: "var(--text-sm)" }}>
            <div style={{ display: "grid", gridTemplateColumns: "max-content 1fr", rowGap: "var(--space-1)", columnGap: "var(--space-3)" }}>
              <span style={{ color: "var(--color-text-muted)" }}>Operator</span><span>{operator}</span>
              <span style={{ color: "var(--color-text-muted)" }}>Period</span><span style={{ fontFamily: "var(--font-family-mono, monospace)" }}>{period}</span>
              <span style={{ color: "var(--color-text-muted)" }}>{feeMode ? "Fee invoice" : "Fees"}</span><span><strong>{amount}</strong> {currency}</span>
              {shape === "payoutonly" && (
                <>
                  <span style={{ color: "var(--color-text-muted)" }}>Note</span>
                  <span style={{ color: "var(--color-text-secondary)" }}>
                    this shape charges no fees — they are settled outside the statement flow
                  </span>
                </>
              )}
              {!feeMode && (
                <>
                  <span style={{ color: "var(--color-text-muted)" }}>Payout</span>
                  <span style={{ color: "var(--color-text-secondary)" }}>
                    collected in the period, less those fees — computed when the bill is drafted
                  </span>
                </>
              )}
            </div>
            <PayoutTargetPanel target={target} payoutExpected={!feeMode} />
            <p style={{ margin: 0, color: "var(--color-text-secondary)" }}>
              Records you as approver and submits the invoice for posting. This cannot be undone here.
            </p>
          </div>
        ),
        options: [{ label: "Approve", value: true, variant: "primary", default: true }],
      });
      if (!ok) return;
      // Set the `Approved` cell → AlternateUpdate → S_Statement_ApprovePayout_SP.
      // The value is just a trigger; the SP builds the real ApprovedJson. The
      // row key (ID) is mapped to @InvoiceID by the AlternateUpdate translate.
      const result = await updateGridCell("statementInvoice", [
        { sColumn: "ID", oValue: id, Type: ParamType.Equal },
        { sColumn: "Approved", oValue: "1", Type: ParamType.Set },
      ]);
      if (result.bError) {
        deps.toast(result.sMessage || "Approval failed", "error");
        return;
      }
      // oData = the SP's affected-row count as the update's success flag:
      // true = approved; false = nothing changed (already approved, not Closed,
      // or outside your operator scope — the SP enforces all three).
      deps.toast(
        result.oData ? "Invoice approved" : "Not approved — already approved, not closed, or outside your scope",
        result.oData ? "success" : "error",
      );
      deps.refresh();
    };

    return <Button variant="primary" size="sm" onClick={onApprove}>Approve</Button>;
  };
}

function statementInvoiceOverride(deps: GridOverrideDeps): GridOverride {
  return { cellDecorators: { Approved: approveDecorator(deps) } };
}

registerGridOverride("statementInvoice", statementInvoiceOverride);
